import { gql } from '@apollo/client';
import { Box, Button, HStack, useDisclosure, useToast } from '@chakra-ui/react';
import { isEmpty } from 'lodash';
import { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Columns } from '~components/displays/DisplayTable/constants';
import {
  DisplaysQueryContext,
  useDisplaysQuery,
} from '~components/displays/DisplayTable/useDisplaysQuery';
import { statusMessageMap, StatusTypes } from '~components/displays/useStatus';
import { BackButton } from '~components/ui/BackButton';
import { useDestructiveAction } from '~components/ui/DestructiveAction';
import { DisplaysDetailStats } from '~components/ui/DisplaysDetailStats';
import { FileUploadButton } from '~components/ui/FileUploadButton';
import { PageHeading } from '~components/ui/PageHeading';
import { PlaylistUpdateInput } from '~graphql/__generated__/types';
import { useAnalyticsReporter } from '~utils/analytics';
import { fromError } from '~utils/errors';
import { MaybePromise, OmitStrict } from '~utils/types';
import { DeletePlaylistsModal } from '../DeletePlaylistsModal';
import { EditPlaylistModal } from '../EditPlaylistModal';
import {
  PlaylistMediaTable,
  usePlaylistMediaTable,
} from '../PlaylistMediaTable/PlaylistMediaTable';
import { SyncDisplaysModal } from '../SyncDisplaysModal';
import { useMediaList } from '../useMediaList';
import { useRemovePlaylistFromDisplays } from '../useRemovePlaylistFromDisplays';
import {
  PlaylistDetail_CustomerFragment,
  PlaylistDetail_PlaylistFragment,
  usePlaylistUpdateMutation,
} from './__generated__/PlaylistDetail.graphql';
import { PlaylistDetailMoreActions } from './PlaylistDetailMoreActions';

interface Props {
  customer: PlaylistDetail_CustomerFragment;
  playlist: PlaylistDetail_PlaylistFragment;
  onPlaylistUpdateSuccess: () => MaybePromise<void>;
}

export type FormValues = OmitStrict<PlaylistUpdateInput, 'mediaIds'>;

export function PlaylistDetail({ customer, playlist, onPlaylistUpdateSuccess }: Props) {
  const toast = useToast();
  const navigate = useNavigate();
  const syncDisplaysModal = useDisclosure();
  const deletePlaylistsModal = useDisclosure();
  const editPlaylistModal = useDisclosure();
  const { removePlaylistFromDisplays } = useRemovePlaylistFromDisplays();
  const analytics = useAnalyticsReporter();
  const { setDisplaysQuery } = useContext(DisplaysQueryContext);

  const handleDeletePlaylistsSuccess = () => {
    deletePlaylistsModal.onClose();
    analytics.track('playlistDelete');
    navigate('../playlists');
  };

  const [updatePlaylist] = usePlaylistUpdateMutation();

  const { mediaList, reinitializeMediaList, updateMedia } = useMediaList({
    customerId: customer.id,
    initialMedia: playlist.media,
  });

  const handleGoBack = () => {
    navigate('../playlists');
  };

  useEffect(() => {
    reinitializeMediaList(playlist.media);
  }, [playlist, reinitializeMediaList]);

  const handleRowMove = async (dragIndex: number, hoverIndex: number) => {
    const dragRecord = mediaList[dragIndex];
    const mediaWithoutDraggedItem = mediaList.filter((_, i) => i !== dragIndex);
    const newList = [
      ...mediaWithoutDraggedItem.slice(0, hoverIndex),
      dragRecord,
      ...mediaWithoutDraggedItem.slice(hoverIndex),
    ];
    const { data } = await updatePlaylist({
      variables: {
        input: {
          playlistId: playlist.id,
          mediaIds: newList.map((m) => m.id),
        },
      },
    });

    if (!data) {
      throw new Error('No data');
    }

    reinitializeMediaList(data.playlistUpdate.playlist.media);
  };

  const performBulkMediaRemoval = async (ids: string[]) => {
    try {
      const updatedList = mediaList.filter((m) => !ids.includes(m.id));

      const { data } = await updatePlaylist({
        variables: {
          input: {
            playlistId: playlist.id,
            title: playlist.title,
            description: playlist.description,
            mediaIds: updatedList.map((m) => m.id),
          },
        },
      });

      if (!data) {
        throw new Error('No data');
      }

      analytics.track('playlistUpdate');

      reinitializeMediaList(data.playlistUpdate.playlist.media);
    } catch (err) {
      toast({
        status: 'error',
        title: 'Cannot edit playlist',
        description: fromError(err, 'EditPlaylist'),
      });
    }
  };

  const table = usePlaylistMediaTable(mediaList, { onRemoveMedia: performBulkMediaRemoval });

  const selectedMedia = table.selectedFlatRows.map((r) => r.original);

  const performMediaUpload = async (fileList: FileList) => {
    try {
      const newMediaList = await updateMedia(fileList);

      const { data } = await updatePlaylist({
        variables: {
          input: {
            playlistId: playlist.id,
            mediaIds: newMediaList.map((m) => m.id),
          },
        },
      });

      if (!data) {
        throw new Error('No data');
      }

      reinitializeMediaList(data.playlistUpdate.playlist.media);
      analytics.track('playlistUpdate');

      await onPlaylistUpdateSuccess();
    } catch (err) {
      toast({
        status: 'error',
        title: 'Cannot edit playlist',
        description: fromError(err, 'EditPlaylist', {
          MEDIA_NOT_FOUND: 'The media you are trying to save has not been found',
          INVALID_MIME_TYPE:
            "Looks like the type of the file you're trying to upload is not supported.",
        }),
      });
    }
  };

  const handlePlaylistEdit = async () => {
    await onPlaylistUpdateSuccess();
    editPlaylistModal.onClose();
    analytics.track('playlistUpdate');
  };

  const removeDisplaysAction = useDestructiveAction<undefined>({
    title: 'Remove playlist from displays',
    message: 'Are you sure you want remove this playlist from all displays?',
    notice: 'The displays will no longer be able to play this content.',
    confirmLabel: 'Remove',
    onConfirm: async () => {
      const displayIds = playlist.syncedDisplays.map((d) => d.id);
      await removePlaylistFromDisplays(displayIds);
    },
  });

  const bulkDeleteMediaAction = useDestructiveAction<undefined>({
    title: 'Delete media',
    message: 'The media you selected will be permanently removed.',
    notice: 'This action cannot be undone.',
    confirmLabel: 'Delete files',
    onConfirm: async () => {
      await performBulkMediaRemoval(selectedMedia.map((m) => m.id));
    },
  });

  const { persistFilters } = useDisplaysQuery();

  const canGoToLinkedDisplays = playlist.syncedDisplays.length > 0;

  const handleGoToLinkedDisplays = (
    playlistTitle: string,
    filters: Partial<Record<Columns, string>> = {},
  ) => {
    persistFilters(
      [
        {
          id: Columns.Playlist,
          value: [{ column: Columns.Playlist, value: playlistTitle, label: playlistTitle }],
        },
        ...Object.entries(filters).reduce<Parameters<typeof persistFilters>[0]>(
          (acc, [key, value]) => {
            acc.push({
              id: key,
              value: [{ column: key, value, label: value }],
            });
            return acc;
          },
          [],
        ),
      ],
      { reset: true },
    );

    const searchParams = new URLSearchParams({ playlist: playlistTitle, ...filters });
    setDisplaysQuery?.(searchParams);

    navigate(`../displays?${searchParams.toString()}`);
  };

  return (
    <>
      <PageHeading
        floatingButton={<BackButton onClick={handleGoBack} />}
        description={playlist.description || undefined}
        actions={
          <HStack>
            <FileUploadButton
              leftIcon={undefined}
              variant="solid"
              colorScheme="blue"
              onChange={performMediaUpload}
            >
              Add media
            </FileUploadButton>
            <PlaylistDetailMoreActions
              playlist={playlist}
              onRename={editPlaylistModal.onOpen}
              onSync={syncDisplaysModal.onOpen}
              onRemoveDisplays={() => removeDisplaysAction.askConfirmation(undefined)}
              onDelete={deletePlaylistsModal.onOpen}
            />
          </HStack>
        }
      >
        {playlist.title}
      </PageHeading>

      <HStack justifyContent="space-between" alignItems="flex-start" marginTop={'8'}>
        <DisplaysDetailStats
          syncedCount={playlist.syncedDisplays.length}
          outOfSyncCount={playlist.outOfSyncDisplays.length}
          syncingCount={playlist.syncingDisplays.length}
          removingCount={playlist.removingDisplays.length}
          onClickSyncedDisplays={
            canGoToLinkedDisplays ? () => handleGoToLinkedDisplays(playlist.title) : undefined
          }
          onClickOutOfSyncDisplays={() =>
            handleGoToLinkedDisplays(playlist.title, {
              [Columns.Status]: statusMessageMap[StatusTypes.PlaylistOutOfSync],
            })
          }
        />
        {!isEmpty(selectedMedia) ? (
          <Button
            variant={'solid'}
            colorScheme={'red'}
            onClick={() => bulkDeleteMediaAction.askConfirmation(undefined)}
          >
            Delete selected
          </Button>
        ) : undefined}
      </HStack>

      <Box
        boxShadow="elevated"
        border="1px solid"
        borderRadius="md"
        borderColor="gray.100"
        overflowX="auto"
        background="white"
        marginTop={2}
      >
        <PlaylistMediaTable
          table={table}
          onRowMove={handleRowMove}
          onFileUpload={performMediaUpload}
        />
      </Box>

      {removeDisplaysAction.confirmationNode}
      {bulkDeleteMediaAction.confirmationNode}
      <SyncDisplaysModal
        isOpen={syncDisplaysModal.isOpen}
        playlist={playlist}
        onSuccess={syncDisplaysModal.onClose}
        onCancel={syncDisplaysModal.onClose}
      />
      <DeletePlaylistsModal
        isOpen={deletePlaylistsModal.isOpen}
        playlists={[playlist]}
        onCancel={deletePlaylistsModal.onClose}
        onSuccess={handleDeletePlaylistsSuccess}
      />
      <EditPlaylistModal
        isOpen={editPlaylistModal.isOpen}
        playlist={playlist}
        onCancel={editPlaylistModal.onClose}
        onSuccess={handlePlaylistEdit}
      />
    </>
  );
}

PlaylistDetail.graphql = {
  fragments: {
    PlaylistDetail_customer: gql`
      fragment PlaylistDetail_customer on Customer {
        id
        name
      }
    `,
    PlaylistDetail_playlist: gql`
      fragment PlaylistDetail_playlist on Playlist {
        id
        title
        description
        media {
          ...PlaylistMediaTable_media
        }
        syncedDisplays: displays(filter: { state: SYNCED }) {
          id
        }
        outOfSyncDisplays: displays(filter: { state: OUT_OF_SYNC }) {
          id
        }
        syncingDisplays: displays(filter: { state: SYNCING }) {
          id
        }
        removingDisplays: displays(filter: { state: REMOVING }) {
          id
        }
        ...SyncDisplaysModal_playlist
      }
    `,
  },
  mutations: {
    PlaylistUpdate: gql`
      mutation PlaylistUpdate($input: PlaylistUpdateInput!) {
        playlistUpdate(input: $input) {
          playlist {
            id
            ...PlaylistDetail_playlist
          }
        }
      }
    `,
  },
};
